<template>
  <div :class="classModifiers" class="sp-date-picker-two">
    <input ref="datepicker" type="hidden" />
  </div>
</template>

<script>
const Mode = {
  Single: "single",
  Range: "range",
  Multiple: "multiple",
};
</script>

<script setup>
import { useEventListener, whenever } from "@vueuse/core";
import flatpickr from "flatpickr";
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  toRef,
  useAttrs,
  useShadowRoot,
  useTemplateRef,
  watch,
} from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useFormControl } from "#ce/composables/useFormControl";
import { useExpose } from "#ce/composables/useExpose";
import { useForm } from "#ce/composables/useForm";
import { useHostEmit } from "#ce/composables/useHostEmit";
import { t } from "#ce/shared/forms-i18n";
import { isPresent, toBoolean } from "#ce/utils/props";
import { props as datePickerProps } from "./props.js";
import { formAssociatedHost } from "#ce/framework/utils";

// sp-date-picker-two is a genuine form control: match :disabled and respond to <fieldset disabled>.
defineOptions(formAssociatedHost());

const emit = defineEmits(["input", "change", "update-model-value"]);
const { emitBubbling } = useHostEmit();

const props = defineProps(datePickerProps);

// Disabled is governed by the form-control composable: presence-based own
// attribute plus ancestor <fieldset disabled>, applied to the flatpickr input.
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

const datepicker = useTemplateRef("datepicker");

const shadowRoot = useShadowRoot();

const { breakpointModifiers } = useBreakpoints();
const classModifiers = computed(() => [...breakpointModifiers.value]);

/**
 * This is a workaround to load the temporary CSS link.
 * It should be removed once full theme support is implemented.
 *
 * TODO: @sleistner, @mgruschetzki
 */
onMounted(() => {
  if (props.tmpCssLink && shadowRoot) {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = props.tmpCssLink;
    shadowRoot.appendChild(link);
  }
});

const { exposeProperties } = useExpose();

const calendar = ref(null);
const altInput = computed(() => calendar.value?.altInput);

// The `value` attribute seeds the calendar and names what a form reset restores; the `value`
// property is the live selection. A declared prop would install a non-configurable host accessor
// that returns the seed forever.
const attrs = useAttrs();
const defaultValue = ref(attrs.value);
const model = ref(defaultValue.value);

exposeProperties({
  displayText: {
    configurable: true,
    get: () => altInput.value?.value,
  },
  value: {
    configurable: true,
    get: () => model.value,
    set: setSelection,
  },
  defaultValue: {
    configurable: true,
    get: () => defaultValue.value,
  },
});

watch(
  () => attrs.value,
  (value) => {
    defaultValue.value = value;
    setSelection(value);
  },
);
watch(model, (value) => {
  if (!value) {
    clearCalendar();
  }
});

// Drives the calendar as well as the model, so a value written from outside is reflected in the
// rendered dates. `false` suppresses flatpickr's own change hook, which would re-enter here.
function setSelection(value) {
  model.value = value ?? null;
  calendar.value?.setDate(model.value, false);
}

// The calendar sits in the shadow root, where the outer form cannot see it, so the element submits
// through ElementInternals under its own name.
const { mirrorFormValue, mirrorValueMissing, onFormReset } = useForm();
const formValue = computed(() => (model.value === undefined || model.value === null ? "" : String(model.value)));

mirrorFormValue(formValue);

onFormReset(() => setSelection(defaultValue.value));

// A required picker is satisfied only once a date is chosen.
const isRequired = computed(() => isPresent(props.required));
const validationMessage = computed(() => props.requiredMessage || t("date_value_missing"));

mirrorValueMissing(() => isRequired.value && formValue.value === "", validationMessage, datepicker);

// clear(false): flatpickr's default fires its own change, which re-enters handleChange and
// reports a scripted clear as user input, twice.
function clearCalendar() {
  calendar.value?.clear(false);
}

const isRangeMode = computed(() => toBoolean(props.range));
const isMultipleMode = computed(() => toBoolean(props.multiple));

const mode = computed(() => {
  if (isMultipleMode.value) {
    return Mode.Multiple;
  }
  if (isRangeMode.value) {
    return Mode.Range;
  }
  return props.mode;
});

function initializeFlatpickr() {
  const config = {
    mode: mode.value,
    inline: toBoolean(props.inline),
    wrap: toBoolean(props.wrap),
    altInput: true,
    altFormat: props.displayFormat,
    altInputClass: "flatpickr-alt-input",
    onChange: handleChange,
    prevArrow: buildArrowIcon("arrow-left-filled"),
    nextArrow: buildArrowIcon("arrow-right-filled"),
  };

  if (props.dayTestId) {
    config.onDayCreate = (_dates, _dateString, _instance, dayElement) => {
      dayElement.setAttribute("data-testid", props.dayTestId);
    };
  }

  calendar.value = flatpickr(datepicker.value, config);
  calendar.value.setDate(model.value, false);

  if (props.inputTestId && calendar.value.altInput) {
    calendar.value.altInput.setAttribute("data-testid", props.inputTestId);
  }

  applyDisabledState(disabled.value);
}

/**
 * Reflects the disabled state onto flatpickr's generated input, prevents the
 * calendar from opening, and closes it if it is already open (inline calendars
 * stay rendered, but `handleChange` is guarded below). CSS dimming is handled
 * separately via :host(:disabled).
 *
 * @param {boolean} isDisabled
 */
function applyDisabledState(isDisabled) {
  if (!calendar.value) {
    return;
  }

  if (calendar.value.altInput) {
    calendar.value.altInput.disabled = isDisabled;
  }
  calendar.value.input.disabled = isDisabled;
  calendar.value.set("clickOpens", !isDisabled);

  if (isDisabled) {
    calendar.value.close();
  }
}

watch(disabled, applyDisabledState);

function buildArrowIcon(name) {
  return `<sp-icon name='${name}' fill-color='var(--date-picker-icon-arrow-color)'></sp-icon>`;
}

/**
 * We wait for the calendar to become available and then delegate the click event from the navigation
 * icons to the parent element.
 *
 * This is necessary because flatpickr does not detect the click event on the icon.
 */
whenever(calendar, (instance) => {
  delegateIconEvent(instance.prevMonthNav);
  delegateIconEvent(instance.nextMonthNav);
});

/**
 * Delegates the click event from the icon to the parent element.
 *
 * @param {HTMLElement} parent - The parent element to which the event should be delegated.
 */
function delegateIconEvent(parent) {
  const target = parent.querySelector("sp-icon");
  useEventListener(target, "click", () => parent.dispatchEvent(new PointerEvent("click", { bubbles: true })));
}

function handleChange(selectedDates, value) {
  // A disabled date picker must not mutate form state, even via an inline or
  // already-rendered calendar whose day cells stay clickable.
  if (disabled.value) {
    return;
  }

  // If the range mode is enabled, but the user has not selected two dates, do not update the model value.
  if (isRangeMode.value && selectedDates.length !== 2) {
    return;
  }

  model.value = value;
  emitBubbling("input", value);
  emit("update-model-value", value);
  emitBubbling("change", value);
}

onMounted(() => {
  nextTick(initializeFlatpickr);
});

onUnmounted(() => {
  calendar.value?.destroy();
});
</script>

<style lang="scss">
@import "flatpickr/dist/flatpickr.min.css";

// Disabled styling driven by the native :disabled state (own attribute or an
// ancestor <fieldset disabled>), no JS required.
:host(:disabled) {
  cursor: not-allowed;
  opacity: 0.6;
}

.sp-date-picker-two {
  --date-picker-icon-arrow-color: var(--sp-comp-date-picker-arrow-icon-color, var(--sp-sys-color-primary));

  &.--is-sm-screen {
    .flatpickr-rContainer {
      display: block !important;
      width: 100% !important;
    }
    .dayContainer,
    .flatpickr-days {
      width: 100% !important;
      min-width: 100% !important;
      max-width: 100% !important;
    }
    .flatpickr-day {
      max-width: 14.2857143% !important; // = 100% / 7 days
    }
  }
}

// flatpickr overrides

.flatpickr-alt-input {
  display: none;
}

.flatpickr-calendar {
  --selected-bg: var(--sp-comp-date-picker-selected-state-bg, var(--sp-sys-color-primary)) !important;
  --selected-text-color: var(
    --sp-comp-date-picker-selected-state-text-color,
    var(--sp-sys-color-on-primary)
  ) !important;

  box-shadow: none !important;
  padding: 0 !important;
  margin: 0 !important;
  font-family: var(--sp-sys-font-family-normal);
  font-weight: var(--sp-sys-font-weight-normal);

  .flatpickr-day {
    display: flex;
    align-items: center;
  }

  .flatpickr-day.selected,
  .flatpickr-day.startRange,
  .flatpickr-day.endRange,
  .flatpickr-day.selected.inRange,
  .flatpickr-day.startRange.inRange,
  .flatpickr-day.endRange.inRange,
  .flatpickr-day.selected:focus,
  .flatpickr-day.startRange:focus,
  .flatpickr-day.endRange:focus,
  .flatpickr-day.selected:hover,
  .flatpickr-day.startRange:hover,
  .flatpickr-day.endRange:hover,
  .flatpickr-day.selected.prevMonthDay,
  .flatpickr-day.startRange.prevMonthDay,
  .flatpickr-day.endRange.prevMonthDay,
  .flatpickr-day.selected.nextMonthDay,
  .flatpickr-day.startRange.nextMonthDay,
  .flatpickr-day.endRange.nextMonthDay {
    background: var(--selected-bg) !important;
    border-color: var(--selected-bg) !important;
    color: var(--selected-text-color) !important;
  }

  .flatpickr-day.inRange,
  .flatpickr-day.prevMonthDay.inRange,
  .flatpickr-day.nextMonthDay.inRange,
  .flatpickr-day.today.inRange,
  .flatpickr-day.prevMonthDay.today.inRange,
  .flatpickr-day.nextMonthDay.today.inRange,
  .flatpickr-day:hover,
  .flatpickr-day.prevMonthDay:hover,
  .flatpickr-day.nextMonthDay:hover,
  .flatpickr-day:focus,
  .flatpickr-day.prevMonthDay:focus,
  .flatpickr-day.nextMonthDay:focus {
    --background: var(--sp-comp-date-picker-in-range-bg, var(--sp-sys-color-primary-container));
    border-color: var(--background) !important;
    background: var(--background) !important;
    color: var(--sp-comp-date-picker-in-range-text-color, var(--sp-sys-color-on-surface, #000)) !important;
    box-shadow:
      -5px 0 0 var(--background),
      5px 0 0 var(--background);
  }

  .flatpickr-day:hover,
  .flatpickr-day.prevMonthDay:hover,
  .flatpickr-day.nextMonthDay:hover,
  .flatpickr-day:focus,
  .flatpickr-day.prevMonthDay:focus,
  .flatpickr-day.nextMonthDay:focus {
    box-shadow: none !important;
  }

  .flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n + 1)),
  .flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n + 1)),
  .flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n + 1)) {
    box-shadow: -10px 0 0 var(--selected-bg);
  }

  .flatpickr-day.today {
    border: 2px solid var(--sp-comp-date-picker-today-border-color, #b30005) !important;
  }

  .flatpickr-current-month {
    position: relative;
    left: unset;
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0;
    font: var(--sp-sys-typescale-small-bold-short) !important;

    .flatpickr-monthDropdown-months {
      padding: 0;
      margin-left: -0.35ch;
    }

    .flatpickr-monthDropdown-months,
    .cur-year {
      font-weight: var(--sp-sys-typescale-small-bold-font-weight) !important;
    }
  }

  .flatpickr-months .flatpickr-prev-month,
  .flatpickr-months .flatpickr-next-month {
    position: relative;
    padding: 0;
    transition: all 0.3s ease-in-out;

    &:hover {
      opacity: 0.7;
    }
  }

  .flatpickr-months {
    margin-bottom: 0 !important;

    .flatpickr-month {
      order: 1;
    }

    .flatpickr-prev-month {
      order: 2;
    }

    .flatpickr-next-month {
      order: 3;
    }
  }
  .flatpickr-weekdays {
    border-bottom: 1px solid var(--sp-comp-date-picker-weekdays-border-color, var(--sp-sys-color-outline-variant));
  }
}
</style>
